import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Inject,
  OnInit,
  signal,
  ViewChild
} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup} from '@angular/forms';
import {MatDialogRef, MAT_DIALOG_DATA} from '@angular/material/dialog';
import {finalize, tap} from 'rxjs/operators';
import {catchError, forkJoin, map, Observable, of, switchMap} from 'rxjs';
import {fromPromise} from 'rxjs/internal/observable/innerFrom';
import {Q9Validators} from '@q9elements/ui-kit/validators';
import {
  PermissionsService,
  Q9ElementsGptService,
  SessionService,
  TakeUntilComponentDestroy
} from '@q9elements/ui-kit/common';
import {Option, Q9CreditBalanceWarningComponent} from '@q9elements/ui-kit/components';
import {ImageMeta} from '@q9elements/ui-kit/maps';

import {ActivityAutogenerateDrilldownParams} from './data-access/models/activity-autogenerate-drilldown-params.interface';
import {
  AIImagePreview,
  AutoGenerateDrilldownPayload
} from './data-access/models/auto-generate-drilldown-payload.interface';
import {ACTIVITY_VALIDATORS, MAX_CONTEXT_LENGTH, MAX_GUIDANCE_LENGTH} from './utils/constants';
import {ImageUploadService, IUpload} from '../core/services/image.upload.service';
import {autoGenerateDrilldownLink} from '../shared/constants/elements-links';
import {SanitizeXssService} from '../core/services/santize-xss.service';
import {get} from 'lodash';

interface ImageMetaWithCreds extends ImageMeta {
  credentials?: IUpload;
}

enum GENERATION_TYPES {
  Text = 'text',
  Image = 'image'
}

@Component({
  selector: 'app-auto-generate-drilldown-dialog',
  templateUrl: 'auto-generate-drilldown-dialog.component.html',
  styleUrls: ['auto-generate-drilldown-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AutoGenerateDrilldownDialogComponent
  extends TakeUntilComponentDestroy
  implements OnInit
{
  @ViewChild('generateForm', {static: true}) container: {nativeElement: HTMLElement};
  @ViewChild('creditWarning') creditWarning!: Q9CreditBalanceWarningComponent;

  private readonly sessionService = inject(SessionService);
  private readonly permissionsService = inject(PermissionsService);

  public readonly isFree = this.permissionsService.isFree;
  public readonly hasSubscription = computed(() => {
    const user = this.sessionService.user();

    return !!get(user, 'userInTeam.team.subscription.subscriptionId');
  });

  public form: FormGroup;
  public images: ImageMetaWithCreds[] = [];
  public imagePreviews: AIImagePreview[] = [];
  public modalTitle: string;

  public readonly enterpriseGenerationEnabled = signal(false);
  public readonly permissionLoading = signal(true);
  public readonly canAttachImages = signal(false);
  public readonly generationTypes = signal<Option[]>([
    {
      value: GENERATION_TYPES.Text,
      label: 'AUTOGENERATE_DRILLDOWN.GENERATE_FROM_TEXT'
    }
  ]);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ActivityAutogenerateDrilldownParams,
    public dialogRef: MatDialogRef<AutoGenerateDrilldownDialogComponent>,
    private fb: FormBuilder,
    private imageSvc: ImageUploadService,
    private sanitizeXssService: SanitizeXssService,
    private generationPermissionService: Q9ElementsGptService
  ) {
    super();
  }

  ngOnInit() {
    this.setupPermissions();
    this.initForm();
    this.imagePreviews = this.data.preset?.previews || [];
  }

  public get isImageGenerationType(): boolean {
    return this.form?.get('generationType')?.value === GENERATION_TYPES.Image;
  }

  public goToArticle() {
    window.open(autoGenerateDrilldownLink);
  }

  public generate(): void {
    if (this.form.invalid || !this.creditWarning?.canProceed()) {
      return;
    }

    const {generationType, ...sanitizedContent} = this.sanitizeXssService.sanitizeXss(
      this.form.value,
      ['activity', 'intentForDiagram', 'guidance']
    );
    const autoGenerateDrilldown: AutoGenerateDrilldownPayload = {
      activityMxKey: this.data.activity.mxKey,
      ...sanitizedContent
    };

    if (generationType === GENERATION_TYPES.Text || !this.images.length) {
      this.dialogRef.close(autoGenerateDrilldown);
    }

    this.prepareImages()
      .pipe(
        map(images => {
          const imagesMeta: {fileType: string; imageKey: string}[] = this.images.map(image => {
            return {
              fileType: image.blob.type,
              imageKey: image.credentials.key
            };
          });

          if (this.imagePreviews.length) {
            imagesMeta.push(
              ...this.imagePreviews.map(({fileType, imageKey}) => ({fileType, imageKey}))
            );
          }

          return {...autoGenerateDrilldown, images: imagesMeta};
        })
      )
      .subscribe((autoGenerateDrilldownWithImages: AutoGenerateDrilldownPayload) => {
        this.dialogRef.close(autoGenerateDrilldownWithImages);
      });
  }

  public scrollToBottom() {
    if (this.container?.nativeElement) {
      setTimeout(
        () => (this.container.nativeElement.scrollTop = this.container.nativeElement.scrollHeight),
        100
      );
    }
  }

  public get intentForDiagramField(): AbstractControl {
    return this.form.get('intentForDiagram');
  }

  private setupPermissions(): void {
    this.generationPermissionService
      .getExternalGptConfig()
      .pipe(
        switchMap(({enabled, validCredentials}) => {
          this.enterpriseGenerationEnabled.set(!enabled || validCredentials);
          this.modalTitle = enabled
            ? 'AUTOGENERATE_DRILLDOWN.GENERATE_DRILLDOWN_EXTERNAL_GPT'
            : 'AUTOGENERATE_DRILLDOWN.GENERATE_DRILLDOWN_ELEMENTS_GPT';

          return this.checkPermissions();
        }),
        finalize(() => this.permissionLoading.set(false)),
        this.takeUntilComponentDestroy()
      )
      .subscribe();
  }

  private initForm(): void {
    const {preset, activity} = this.data;

    this.form = this.fb.group({
      generationType: [GENERATION_TYPES.Text],
      activity: [
        preset?.activity ?? this.removeTags(activity.text),
        [
          Q9Validators.required(),
          Q9Validators.minLength(ACTIVITY_VALIDATORS.MIN_LENGTH),
          Q9Validators.maxLength(ACTIVITY_VALIDATORS.MAX_LENGTH)
        ]
      ],
      intentForDiagram: [
        preset?.intentForDiagram ?? '',
        Q9Validators.maxLength(MAX_CONTEXT_LENGTH)
      ],
      guidance: [preset?.guidance ?? '', Q9Validators.maxLength(MAX_GUIDANCE_LENGTH)],
      skipNewResources: [false]
    });
  }

  private checkPermissions(): Observable<{canAttachImages: boolean}> {
    return this.generationPermissionService.generationPermissions().pipe(
      catchError(() => of({canAttachImages: true})),
      tap(({canAttachImages}) => {
        this.canAttachImages.set(canAttachImages);

        if (canAttachImages) {
          this.generationTypes.mutate(generationTypes => {
            generationTypes.push({
              value: GENERATION_TYPES.Image,
              label: 'AUTOGENERATE_DRILLDOWN.GENERATE_FROM_IMAGE'
            });
          });
        }
      })
    );
  }

  private removeTags(str: string): string {
    return (
      str
        ?.replace(/<[^>]+>/g, '')
        .replace(/&quot;/g, '"')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&#39;/g, `'`)
        .replace(/&lt;/g, '<')
        .replace(/&#x3D;/g, '=')
        .replace(/&#x60;/g, '`')
        .replace(/&#x2F;/g, '/')
        .replace(/&gt;/g, '>')
        .trim() || ''
    );
  }

  private prepareImages(): Observable<any> {
    return !this.images.length
      ? of([])
      : forkJoin(this.images.map(image => this.uploadImageToClam(image)));
  }

  private uploadImageToClam(image: ImageMetaWithCreds): Observable<IUpload> {
    let credentials: IUpload;

    return fromPromise(this.imageSvc.drilldownImageUploadUrl(image.blob.type)).pipe(
      tap(creds => (image.credentials = creds)),
      switchMap(({signedUrl}) => this.imageSvc.uploadImage(signedUrl, image.blob)),
      map(() => credentials)
    );
  }
}
